import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import PackageDateEditMainDates from 'components/PackageDateEditMainDates';
import PackageDateEditMainPrices from 'components/PackageDateEditMainPrices';

import {
  dateFieldValue,
  dateString,
  stringToNumber,
  textFieldValue,
  useErrorHandler,
} from 'lib';
import { Range } from 'components/DateRangePicker';

import { PackageDateEditMain_packageDate$key } from '__generated__/PackageDateEditMain_packageDate.graphql';
import { PackageDateEditMainMutation } from '__generated__/PackageDateEditMainMutation.graphql';

type FormValues = {
  dates: Range;
  price: string;
  priceOld: string;
};

type Props = {
  packageDate: PackageDateEditMain_packageDate$key;
};

const PackageDateEditMain: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { id, start, end, price, priceOld } = useFragment(
    graphql`
      fragment PackageDateEditMain_packageDate on PackageDate {
        id
        start
        end
        price
        priceOld
      }
    `,
    props.packageDate
  );

  const [update, updating] = useMutation<PackageDateEditMainMutation>(graphql`
    mutation PackageDateEditMainMutation($input: UpdatePackageDateInput!) {
      updatePackageDate(input: $input) {
        ...PackageDateEditTabs_packageDate
        ...PackageDateSidebar_packageDate
      }
    }
  `);

  const initial = {
    dates: {
      start: dateFieldValue(start),
      end: dateFieldValue(end),
    },
    price: textFieldValue(price),
    priceOld: textFieldValue(priceOld),
  };

  const handleSubmit = useCallback(
    (values: FormValues) => {
      update({
        variables: {
          input: {
            id,
            start: dateString(values.dates.start),
            end: dateString(values.dates.end),
            priceOld: stringToNumber(values.priceOld),
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="packageDateMain">
          <PackageDateEditMainDates />
          <PackageDateEditMainPrices />
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default PackageDateEditMain;
